import {
  ControlCode,
  ControlCodes,
  Account,
  isCurrentUserPerm,
  useAccountQuery,
  useAccountControls,
  Platform,
  Control,
  Permissions,
  ExitCodes,
  ExitCodeGroup,
  ExitCode,
  parseDate,
  NonArchPlatform,
  getControlName,
} from "@theprelude/core";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  IconButton,
  SignalIcon,
  TooltipContent,
  ExternalLinkIcon,
  Button,
  CalendarIcon,
  ControlIcon,
  InfoIcon,
} from "@theprelude/ds";
import { addMinutes, subMinutes } from "date-fns";
import { ReactNode } from "react";
import { DefaultTooltip } from "./default-tooltip";
import { ExternalLink } from "./external-link";

const getDeepLink = (
  control: ControlCode,
  id: string,
  controls?: Control[],
) => {
  if (!controls) return;

  const link = controls.find((c) => c.id === control)?.api;

  if (!link) return;

  if (control === ControlCodes.CROWDSTRIKE) {
    const url = new URL(
      `${link.replace("api", "falcon")}/activity-v2/detections`,
    );
    url.searchParams.set(
      "filter",
      `agent_id:'${id.toLowerCase().replaceAll("-", "")}'`,
    );

    return url.toString();
  }

  if (control === ControlCodes.DEFENDER) {
    return `https://security.microsoft.com/machines/v2/${id}`;
  }

  if (control === ControlCodes.SENTINELONE) {
    return `${link}/sentinels/devices/workstations?page=1&filter={%22ids%22:%22${id}%22}`;
  }
};

const getDetectionDeepLink = (
  control: ControlCode,
  id: string,
  controls: Control[],
  date: string,
) => {
  const link = controls.find((c) => c.id === control)?.api;

  const dateObj = parseDate(date);
  const startDate = subMinutes(dateObj, 3);
  const endDate = addMinutes(dateObj, 3);
  if (control === ControlCodes.CROWDSTRIKE && link) {
    const url = new URL(
      `${link.replace("api", "falcon")}/activity-v2/detections`,
    );
    url.searchParams.set(
      "filter",
      `agent_id:'${id.toLowerCase().replaceAll("-", "")}'+timestamp:'${startDate.toUTCString()}--${endDate.toUTCString()}'`,
    );

    return url.toString();
  }
};

function canUserSeeLink(account: Account) {
  return (
    isCurrentUserPerm(Permissions.EXECUTIVE, account) ||
    isCurrentUserPerm(Permissions.ADMIN, account)
  );
}

export const DetectionDeepLink: React.FC<{
  control?: ControlCode;
  edrID?: string | null;
  date: string;
}> = ({ control, edrID, date }) => {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  if (!control || !edrID) return null;

  const isShowing = controls && canSeeLink;

  if (!isShowing) return null;

  const link = getDetectionDeepLink(control, edrID, controls, date);

  if (!link) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <a href={link} target="_blank" rel="noreferrer">
            <IconButton icon={<SignalIcon />} />
          </a>
        </TooltipTrigger>
        <TooltipContent>View detections</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const getEventDeepLink = (
  control: ControlCode,
  testId: string,
  edrId: string,
  controls: Control[],
  date: string,
) => {
  const link = controls.find((c) => c.id === control)?.api;

  const dateInMs = new Date(date).getTime();
  const start = dateInMs - 180_000;
  const end = dateInMs + 180_000;

  if (control === ControlCodes.CROWDSTRIKE && link) {
    const url = new URL(`${link.replace("api", "falcon")}/investigate/search`);
    url.searchParams.set("start", start.toString());
    url.searchParams.set("end", end.toString());
    url.searchParams.set(
      "query",
      `(#repo=base_sensor or #repo=detections) 
    | #event_simpleName != "Event_DetectionSummaryEvent"
    | CName := ComputerName | CName := Hostname
    | PreludeTestId := "${testId}"
    | format(format="%s %s %s %s %s %s", field=[FileName, ImageFileName, ParentBaseFileName, ParentImageFileName, GrandParentbaseFileName, GrandParentImageFileName], as="PreludePotentialTestIds")
    | PreludePotentialTestIds=/${testId}/
    | EpId := if(length(aid) > 0, then=aid, else=(if(length(AgentId) > 0, then=AgentId, else=AgentIdString) ) ) | EpId = ${edrId}
    | GP := if(length(GrandParentBaseFileName) > 0, then=GrandParentBaseFileName, else=replace(field=GrandParentImageFileName,regex="^.+[\/]"))
    | P := if(length(ParentBaseFileName) > 0, then=ParentBaseFileName, else=replace(field=ParentImageFileName,regex="^.+[\/]"))
    | P := if(length(P) > 0, then=P, else=if(length(ContextBaseFileName) > 0, then=ContextBaseFileName, else=replace(field=ContextImageFileName,regex="^.+[\/]")))
    | F := if(length(FileName) > 0, then=FileName, else=replace(field=ImageFileName,regex="^.+[\/]"))
    | ProcessLineage := if(length(GP) > 0, then=format(format="%s > %s > %s", field=[GP, P, F]), else=format(format="%s > %s", field=[P, F]))
    | default(value="",field=PatternDispositionDescription)
    | PreludeEventType := if( #repo=="base_sensor", then=if( regex("^CustomIOA", field=#event_simpleName), then="detected", else=if( regex("Blocked|Quaran", field=#event_simpleName), then="prevented", else="observed") ), else=if( regex("(?i)(Quarantine|Blocked|Prevent|Suspend|Kill)", field=PatternDispositionDescription), then=if(regex("(?i)(fail)", field=PatternDispositionDescription),then="detected", else="prevented"), else="detected" ) )
    | select([@timestamp,  CName, PreludeEventType, #event_simpleName, ProcessLineage, EpId, PreludeTestId,@rawstring]) | sort(@timestamp, order=asc)`,
    );
    return url.toString();
  }
};

export const EventDeepLink: React.FC<{
  date: string;
  control?: ControlCode;
  edrID?: string | null;
  device_name: string;
  testId?: string;
  dos?: Platform;
}> = ({ control, edrID, testId, date }) => {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  if (!control || !edrID || !testId) return null;

  const isShowing = controls && canSeeLink;

  if (!isShowing) return null;

  const link = getEventDeepLink(control, testId, edrID, controls, date);

  if (!link) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <a href={link} target="_blank" rel="noreferrer">
            <IconButton icon={<CalendarIcon />} />
          </a>
        </TooltipTrigger>
        <TooltipContent>View events</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const DeepLink: React.FC<{ control: ControlCode; edrID: string }> = ({
  control,
  edrID,
}) => {
  const account = useAccountQuery();
  const canSeeLink =
    isCurrentUserPerm(Permissions.EXECUTIVE, account.data) ||
    isCurrentUserPerm(Permissions.ADMIN, account.data);

  const link = getDeepLink(control, edrID, account.data?.controls);

  return (
    <div className="flex items-center gap-2" data-testid="edr-deep-link">
      <ControlIcon className="h-4 w-4 flex-shrink-0" control={control} />
      <span className="flex max-w-md flex-wrap break-all"> {edrID}</span>
      {link && canSeeLink && <ExternalLink href={link} />}
    </div>
  );
};

const getPolicyDeepLink = (
  controls: Control[],
  policy: string,
  control?: ControlCode,
) => {
  const link = controls.find((c) => c.id === control)?.api;

  if (!link) return;

  if (control === ControlCodes.CROWDSTRIKE) {
    const base = new URL(link?.replace("api", "falcon") ?? "");
    const url = new URL(`/configuration/prevention/policies/${policy}`, base);

    return url.toString();
  }
};

export const DeepPolicyLink: React.FC<{
  control?: ControlCode;
  policy: string;
}> = ({ control, policy }) => {
  const account = useAccountQuery();

  if (!control) return null;

  const link = getPolicyDeepLink(account.data?.controls ?? [], policy, control);

  if (!link) return null;

  return <ExternalLink href={link} />;
};

const getThreatHuntDeepLink = (
  controlCode: ControlCode,
  controls: Control[],
  query: string,
) => {
  const control = controls.find((c) => c.id === controlCode);
  if (!control) return;

  if (controlCode === ControlCodes.CROWDSTRIKE) {
    const url = new URL(
      `${control.api.replace("api", "falcon")}/investigate/search`,
    );
    url.searchParams.set("end", "");
    url.searchParams.set("query", query);
    url.searchParams.set("repo", "all");
    url.searchParams.set("start", "30d");
    return url.toString();
  }

  if (controlCode === ControlCodes.DEFENDER) {
    const url = new URL("https://security.microsoft.com/v2/advanced-hunting");
    url.searchParams.set("tid", control.username);
    return url.toString();
  }
};

export const ThreatHuntDeepLink: React.FC<{
  control?: ControlCode;
  query: string;
}> = ({ control, query }) => {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  if (!control) return null;

  const isShowing = controls && canSeeLink;

  if (!isShowing) return null;

  const link = getThreatHuntDeepLink(control, controls, query);

  if (!link) return null;

  return (
    <a href={link} target="_blank" rel="noreferrer">
      <Button
        intent="secondary"
        icon={<ExternalLinkIcon />}
        iconPosition="right"
      >
        Hunt in {getControlName(control)}
      </Button>
    </a>
  );
};

type DeepLinkActionsProps = {
  control: ControlCode | undefined;
  edrId: string | undefined;
  testId: string | undefined;
  host: string | undefined;
  dos: Platform;
  status: ExitCode;
  date: string;
};

const getAdvisoryDeepLink = (
  control: ControlCode,
  controls: Control[],
  slug: string,
) => {
  const link = controls.find((c) => c.id === control)?.api;

  if (!link) return;

  if (control === ControlCodes.CROWDSTRIKE) {
    const url = new URL(
      `${link.replace("api", "falcon")}/intelligence-v2/reports/${slug}`,
    );
    return url.toString();
  }
};

export const AdvisoryDeepLink: React.FC<{
  children: ReactNode;
  control?: ControlCode;
  slug: string;
  className?: string;
}> = ({ children, className, control, slug }) => {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  if (!control) return null;

  const isShowing = controls && canSeeLink;
  if (!isShowing) return null;

  const link = getAdvisoryDeepLink(control, controls, slug);
  if (!link) return null;

  return (
    <a href={link} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
};

const getPolicySettingsDeepLink = ({
  controlCode,
  controls,
  platform,
  policyId,
  settingId,
}: {
  controlCode: ControlCode;
  controls: Control[];
  platform?: NonArchPlatform;
  policyId: string;
  settingId?: string;
}) => {
  const control = controls.find((c) => c.id === controlCode);
  if (!control) return;

  if (controlCode === ControlCodes.CROWDSTRIKE) {
    const host = control.api.replace("api", "falcon");
    const url = new URL(
      `${host}/policies/prevention/${platform === "darwin" ? "mac" : platform}/detail/${policyId}/Settings`,
    );
    if (settingId) {
      url.hash = settingId;
    }
    return url.toString();
  }

  if (controlCode === ControlCodes.DEFENDER) {
    const url = new URL(
      `https://security.microsoft.com/policy-inventory/${policyId}`,
    );
    url.searchParams.set("implementationType", "DcV2");
    url.searchParams.set("viewid", "settingsValues");
    url.searchParams.set("tid", control.username);
    return url.toString();
  }

  if (controlCode === ControlCodes.SENTINELONE) {
    const groupId = policyId.split("/").at(-1) ?? "";
    const url = new URL(`${control.api}/sentinels/policy/workstations/default`);
    url.searchParams.set("groupIds", groupId);
    return url.toString();
  }

  if (controlCode === ControlCodes.ENTRA) {
    const url = new URL("https://intune.microsoft.com/");
    url.hash = `view/Microsoft_AAD_UsersAndTenants/UserManagementMenuBlade/~/UserSettings/menuId/`;
    return url.toString();
  }

  if (controlCode === ControlCodes.INTUNE) {
    const url = new URL("https://intune.microsoft.com/");
    url.hash = `view/Microsoft_Intune_DeviceSettings/DevicesComplianceMenu/~/policies`;
    return url.toString();
  }

  if (controlCode === ControlCodes.OKTA) {
    const url = new URL(
      `${control.api.replace("-admin", "").replace(".okta.com", "-admin.okta.com")}admin/access/authenticators/password`,
    );
    return url.toString();
  }
};

export function PolicySettingsDeepLink({
  children,
  control,
  className,
  platform,
  policyId,
  settingId,
}: {
  children: ReactNode;
  control?: ControlCode;
  className?: string;
  platform?: NonArchPlatform;
  policyId: string;
  settingId?: string;
}) {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  if (!control) return null;

  const isShowing = controls && canSeeLink;
  if (!isShowing) return null;

  const link = getPolicySettingsDeepLink({
    controlCode: control,
    controls,
    platform,
    policyId,
    settingId,
  });
  if (!link) return null;

  return (
    <a href={link} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

export function PolicySettingsDocLink({ control }: { control: ControlCode }) {
  let link;

  if (control === ControlCodes.DEFENDER) {
    link =
      "https://learn.microsoft.com/en-us/windows/client-management/mdm/policy-csp-defender";
  }
  if (control === ControlCodes.CROWDSTRIKE) {
    link =
      "https://falcon.us-2.crowdstrike.com/documentation/page/e5c21607/prevention-policy-settings";
  }
  if (control === ControlCodes.SENTINELONE) {
    link =
      "https://usea1-partners.sentinelone.net/docs/en/default-and-recommended-policy-settings.html#default-and-recommended-policy-settings";
  }
  if (!link) {
    return null;
  }
  return (
    <DefaultTooltip
      asChild
      content={`${control === ControlCodes.DEFENDER ? "MDE AV" : `${getControlName(control)}`} policy descriptions`}
    >
      <a href={link} target="_blank" rel="noreferrer">
        <IconButton icon={<InfoIcon />} />
      </a>
    </DefaultTooltip>
  );
}

const getPolicyHostsDeepLink = (
  controlCode: ControlCode,
  controls: Control[],
  policyId: string,
) => {
  const control = controls.find((c) => c.id === controlCode);
  if (!control) return;

  if (controlCode === ControlCodes.CROWDSTRIKE) {
    const url = new URL(
      `${control.api.replace("api", "falcon")}/host-management/hosts`,
    );
    url.searchParams.set("filter", policyId);
    return url.toString();
  }

  if (controlCode === ControlCodes.DEFENDER) {
    const url = new URL(
      `https://security.microsoft.com/policy-inventory/${policyId}`,
    );
    url.searchParams.set("implementationType", "DcV2");
    url.searchParams.set("viewid", "devices");
    url.searchParams.set("tid", control.username);
    return url.toString();
  }

  if (controlCode === ControlCodes.SENTINELONE) {
    const groupId = policyId.split("/").at(-1) ?? "";
    const url = new URL(`${control.api}/sentinels/devices/workstations`);
    url.searchParams.set("page", "1");
    url.searchParams.set(
      "filter",
      JSON.stringify({ filteredGroupIds: groupId }),
    );
    return url.toString();
  }
};

const getMissingMFADeepLink = (
  controlCode: ControlCode,
  controls: Control[],
) => {
  const control = controls.find((c) => c.id === controlCode);
  if (!control) return;

  if (controlCode === ControlCodes.OKTA) {
    return new URL(`${control.api}/analytics/reports/mfa-users`).toString();
  }
};

export function PolicyHostsDeepLink({
  children,
  control,
  className,
  policyId,
}: {
  children: ReactNode;
  control?: ControlCode;
  className?: string;
  policyId: string;
}) {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  if (!control) return null;

  const isShowing = controls && canSeeLink;
  if (!isShowing) return null;

  const link = getPolicyHostsDeepLink(
    control,
    controls,
    control === ControlCodes.CROWDSTRIKE
      ? `device_policies.prevention:'${policyId}'`
      : policyId,
  );
  if (!link) return null;

  return (
    <a href={link} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

export function PolicyHostsReducedFunctionalityDeepLink({
  children,
  control,
  className,
}: {
  children: ReactNode;
  control?: ControlCode;
  className?: string;
}) {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  if (!control || control !== ControlCodes.CROWDSTRIKE) return null;

  const isShowing = controls && canSeeLink;
  if (!isShowing) return null;

  const link = getPolicyHostsDeepLink(
    control,
    controls,
    "reduced_functionality_mode:'yes'",
  );
  if (!link) return null;

  return (
    <a href={link} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

export function MissingMFADeepLink({
  children,
  control,
  className,
}: {
  children: ReactNode;
  control?: ControlCode;
  className?: string;
}) {
  const { data: canSeeLink } = useAccountQuery(canUserSeeLink);
  const { data: controls } = useAccountControls();

  const isShowing = control && controls && canSeeLink;
  if (!isShowing) return null;

  const link = getMissingMFADeepLink(control, controls);
  if (!link) return null;

  return (
    <a href={link} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

export function DeepLinkActionsColumn({
  control,
  edrId,
  testId,
  host,
  dos,
  status,
  date,
}: DeepLinkActionsProps) {
  const hideDetectionDeepLink = (
    [
      ExitCodes.TEST_NOT_RELEVANT,
      ExitCodes.ENDPOINT_NOT_RELEVANT,
      ...ExitCodeGroup.ERROR,
    ] as unknown as number[]
  ).includes(status);

  if (!control || !edrId || !testId || !host || hideDetectionDeepLink)
    return null;

  return (
    <div className="inline-flex w-full items-center justify-end gap-3">
      <EventDeepLink
        control={control}
        edrID={edrId}
        testId={testId}
        device_name={host}
        dos={dos}
        date={date}
      />
      <DetectionDeepLink control={control} edrID={edrId} date={date} />
    </div>
  );
}
